// Die Bildschirmtabelle des Anwendungsrahmens (ST-UX-001).
//
// Reines Modell, kein React, kein DOM. Der Grund ist der Nachweis: was hier
// steht, ist ohne Browser pruefbar -- und in dieser Umgebung gibt es keinen.
//
// DIE TABELLE IST DIE EHRLICHKEITSGRENZE. Die Navigation entsteht aus ihr und
// nirgendwo sonst. Einen Eintrag "Suche" hinzuschreiben, hinter dem keine Route
// liegt, ist damit nicht nachlaessig, sondern unmoeglich: der Eintrag braucht
// einen `path`, und der Rahmen wuerde ihn aufloesen wollen. ADR-0002 verbietet
// Attrappen; eine Struktur, die sie ausschliesst, ist mehr wert als ein Verbot,
// an das sich jemand erinnern muss.

/**
 * Was ein Bildschirm mindestens verlangt.
 *
 * `signed-in` heisst: eine gueltige Anmeldung genuegt. Ein Rollencode heisst:
 * der Server verlangt diese Rolle, und der Rahmen weiss das.
 */
export type ScreenRequirement = 'signed-in' | { readonly roleCode: string };

export interface Screen {
  readonly id: string;
  /**
   * Adresse. Sie steht in der Adresszeile und ueberlebt einen Reload.
   *
   * Ein Segment der Form `{name}` ist ein PLATZHALTER und passt auf genau ein
   * Segment. Dieselbe Schreibweise wie im Routenvertrag der API
   * (apps/api/src/http/route-path.ts), und aus demselben Grund gewaehlt: zwei
   * Notationen fuer dieselbe Sache waeren zwei Vokabulare.
   */
  readonly path: string;
  /**
   * Beschriftung in der Navigation. Fachsprache, kein Technikbegriff
   * (NFR-UX-003).
   *
   * OPTIONAL, und das Fehlen ist eine AUSSAGE: dieser Bildschirm hat eine
   * Adresse, aber kein Ziel in der Navigation. Genau das braucht eine
   * Detailansicht -- man erreicht sie aus einer Liste oder ueber einen Link,
   * nie aus einem Menue, in dem kein bestimmter Kontakt gemeint sein kann.
   * Ein Menuepunkt "Kontakt" ohne Kennung waere eine Attrappe, und ADR-0002
   * verbietet sie.
   */
  readonly navLabel?: string;
  /**
   * Die Gruppe in der Seitennavigation. Arbeit, die jeden Tag anfaellt, steht
   * oben; Pflegeaufgaben stehen gesammelt darunter, damit sie auffindbar sind,
   * ohne den Alltag zu ueberlagern.
   */
  readonly navGroup?: NavGroupId;
  /** Ueberschrift des Bildschirms und Grundlage des Fenstertitels. */
  readonly title: string;
  readonly requires: ScreenRequirement;
  /** Eine fruehere Adresse: der Rahmen ersetzt sie ohne Verlaufseintrag durch diese. */
  readonly umleitenNach?: string;
}

/**
 * Der Rollencode der Schnellerfassung.
 *
 * Abgeschrieben aus `apps/api/src/policy/party-rules.ts` -- und genau deshalb
 * liest ein Test ihn dort und vergleicht. Eine Abschrift ohne Gegenprobe laeuft
 * auseinander, ohne dass es auffaellt; dasselbe Muster wie die Grundcodes in
 * `access.test.ts`.
 */
export const PARTY_CAPTURE_ROLE_CODE = 'Liaison.Party.Capture.Write';

/**
 * Der Rollencode des Lesewegs.
 *
 * Heute derselbe Wert -- und das ist keine Nachlaessigkeit, sondern die
 * Abbildung einer benannten Entscheidung: `PARTY_READ_ROLE_CODE` in
 * `apps/api/src/policy/party-rules.ts` verweist aus demselben Grund auf die
 * Erfassungsrolle, weil eine eigene Leserolle ihre Registrierung unter
 * `infra/entra/` braucht und der Leseweg bis dahin sonst fuer niemanden
 * betretbar waere. Ein Test liest beide Konstanten dort und vergleicht; wird
 * die Leserolle spaeter eigenstaendig, aendert sich hier genau eine Zeile.
 */
export const PARTY_READ_ROLE_CODE = 'Liaison.Party.Capture.Write';

/**
 * Alle Bildschirme, die es WIRKLICH gibt.
 *
 * Zwei. Nicht, weil der Rahmen nicht mehr traegt, sondern weil es nicht mehr
 * gibt. Die Liste waechst mit den Domaenenslices und nicht mit der Vorstellung
 * davon, wie ein CRM aussieht.
 */
export const SCREENS: readonly Screen[] = Object.freeze([
  // "Dein Zugang" ist eine Auskunft ueber die eigene Rolle und keine Arbeit:
  // erreichbar ueber das Kontomenue, nicht ueber die Navigation. Die Adresse
  // "/" zeigt die Kontakte (siehe resolve).
  Object.freeze({
    id: 'zugang',
    path: '/zugang',
    title: 'Dein Zugang',
    requires: 'signed-in',
  }),
  // Kein navLabel: "Kontakt anlegen" ist die Hauptaktion des Kopfes und von
  // jeder Seite aus erreichbar; ein zweiter Eintrag in der Navigation waere
  // dieselbe Handlung an zwei Stellen.
  Object.freeze({
    id: 'kontakt-anlegen',
    path: '/kontakt-anlegen',
    title: 'Kontakt anlegen',
    requires: Object.freeze({ roleCode: PARTY_CAPTURE_ROLE_CODE }),
  }),
  // Die beiden Bildschirme des Lesewegs (ST-SEA-001, erster Schnitt).
  //
  // "Kontakte" und nicht "Suche": es gibt keine Suche, und ein Menuepunkt, der
  // eine verspricht, waere die Attrappe, die diese Tabelle ausschliesst. Was es
  // gibt, ist eine Liste der Kontakte, die diese Person sehen darf.
  Object.freeze({
    id: 'kontakte',
    path: '/kontakte',
    navLabel: 'Kontakte',
    navGroup: 'arbeit',
    title: 'Kontakte',
    requires: Object.freeze({ roleCode: PARTY_READ_ROLE_CODE }),
  }),
  // Die fruehere Adresse der vollstaendigen Liste. Seit L13-01 blaettert
  // "Kontakte" selbst; die Adresse bleibt, damit Lesezeichen und alte Verweise
  // halten, und leitet um.
  //
  // VOR 'kontakt', weil `/kontakte/{partyId}` dieselbe Segmentzahl hat und die
  // Reihenfolge entscheidet. Eine Party-ID ist eine UUID und nie "alle".
  Object.freeze({
    id: 'kontakte-alle',
    path: '/kontakte/alle',
    title: 'Kontakte',
    requires: Object.freeze({ roleCode: PARTY_READ_ROLE_CODE }),
    umleitenNach: '/kontakte',
  }),
  // Die eigenen Exporte (L13-01a). Kein navLabel: eine Auskunft ueber die
  // eigene Arbeit wie "Dein Zugang", erreichbar ueber das Kontomenue und die
  // Meldung nach dem Anlegen. Dieselbe Rolle wie die Kontakte, aus denen sie
  // entstehen.
  Object.freeze({
    id: 'exporte',
    path: '/exporte',
    title: 'Meine Exporte',
    requires: Object.freeze({ roleCode: PARTY_READ_ROLE_CODE }),
  }),
  Object.freeze({
    id: 'kontakt',
    path: '/kontakte/{partyId}',
    // Kein navLabel: siehe Screen.navLabel. Erreichbar aus der Liste und ueber
    // einen kopierten Link, nicht aus dem Menue.
    title: 'Kontakt',
    requires: Object.freeze({ roleCode: PARTY_READ_ROLE_CODE }),
  }),
  // Die eigenen offenen Wiedervorlagen (ST-ACT-001, zweiter Schnitt;
  // ADR-0052, Entscheidung 6).
  //
  // MIT navLabel, und das ist der Unterschied zu den Detailbildschirmen
  // darunter: "Wiedervorlagen" meint etwas Bestimmtes, naemlich MEINE
  // offenen -- kein Kontakt muss dafuer gewaehlt sein. Der Bildschirm ist der
  // einzige Ort, an dem jemand sieht, was heute ansteht, und er ist deshalb
  // aus dem Menue erreichbar.
  //
  // Die SCHREIBROLLE: die Liste zeigt nicht nur, sie hakt auch ab.
  Object.freeze({
    id: 'wiedervorlagen',
    path: '/wiedervorlagen',
    navLabel: 'Wiedervorlagen',
    navGroup: 'arbeit',
    title: 'Wiedervorlagen',
    requires: Object.freeze({ roleCode: PARTY_CAPTURE_ROLE_CODE }),
  }),
  // Die eigenen Sammlungen (ST-SEA-002 Plan F3, Schnitt 3a; ADR-0065). OHNE
  // Zahl im Menue: es gibt viele benannte Sammlungen, und eine Gesamtzahl
  // kostete auf jeder Seite die Sichtbarkeitsregel ueber alle Mitglieder.
  // Die Schreibrolle wie die Routen (Regel party.collection).
  Object.freeze({
    id: 'sammlungen',
    path: '/sammlungen',
    navLabel: 'Sammlungen',
    navGroup: 'arbeit',
    title: 'Sammlungen',
    requires: Object.freeze({ roleCode: PARTY_CAPTURE_ROLE_CODE }),
  }),
  Object.freeze({
    id: 'sammlung',
    path: '/sammlungen/{collectionId}',
    title: 'Sammlung',
    requires: Object.freeze({ roleCode: PARTY_CAPTURE_ROLE_CODE }),
  }),
  // Der Brevo-Testversand an die eigene Adresse (ST-BRV-001 Schnitt B2). Die
  // Erfassungsrolle wie die Regel campaign.test_send_create (benannte
  // Uebergangsausnahme, decisions ST-BRV-001).
  Object.freeze({
    id: 'testversand',
    path: '/testversand',
    navLabel: 'Testversand',
    navGroup: 'versand',
    title: 'Testversand',
    requires: Object.freeze({ roleCode: PARTY_CAPTURE_ROLE_CODE }),
  }),
  // Die Massenpflege von Stichworten (ST-TAX-002, Batch-Schnitt; ADR-0058
  // Ziffer 13). In der Gruppe "Datenpflege": eine seltene Aufgabe, die ohne
  // Menuepunkt nur ueber drei Klicks aus der Kontaktsuche zu finden war. Die
  // Gruppe haelt sie vom Alltag getrennt, ohne sie zu verstecken (ST-UX-001,
  // decisions). Weiterhin auch ueber den geteilten Link zur Freigabe.
  Object.freeze({
    id: 'massenpflege',
    path: '/massenpflege',
    navLabel: 'Massenpflege',
    navGroup: 'datenpflege',
    title: 'Massenpflege',
    requires: Object.freeze({ roleCode: PARTY_CAPTURE_ROLE_CODE }),
  }),
  Object.freeze({
    id: 'massenlauf',
    path: '/massenpflege/{batchId}',
    title: 'Massenlauf',
    requires: Object.freeze({ roleCode: PARTY_CAPTURE_ROLE_CODE }),
  }),
  // Der Stichwortkatalog (ST-TAX-003, MVP-Kern), in der Gruppe "Datenpflege"
  // aus demselben Grund wie die Massenpflege.
  Object.freeze({
    id: 'stichwortkatalog',
    path: '/stichwortkatalog',
    navLabel: 'Stichwortkatalog',
    navGroup: 'datenpflege',
    title: 'Stichwortkatalog',
    requires: Object.freeze({ roleCode: PARTY_CAPTURE_ROLE_CODE }),
  }),
  // Der Rollenkatalog (ADR-0067) neben dem Stichwortkatalog: Lesen und
  // Pflegen verlangen dieselbe Rolle, ohne sie gibt es keinen Eintrag.
  Object.freeze({
    id: 'rollenkatalog',
    path: '/rollenkatalog',
    navLabel: 'Rollenkatalog',
    navGroup: 'datenpflege',
    title: 'Rollenkatalog',
    requires: Object.freeze({ roleCode: PARTY_CAPTURE_ROLE_CODE }),
  }),
  // Die eigenen Datenqualitaetsfaelle (ST-TAX-002 B10, ADR-0059, D5), in der
  // Gruppe "Datenpflege": ein Fall, den niemand findet, bleibt liegen.
  Object.freeze({
    id: 'datenqualitaet',
    path: '/datenqualitaet',
    navLabel: 'Stichwortkonflikte',
    navGroup: 'datenpflege',
    title: 'Stichwortkonflikte',
    requires: Object.freeze({ roleCode: PARTY_CAPTURE_ROLE_CODE }),
  }),
  // Die Pflege der Kontaktwege (ST-PTY-002, erster Schnitt).
  //
  // EIGENER BILDSCHIRM UND KEIN MODUS DES DETAILS: Kapitel 5.2 Nr. 2 verlangt
  // EINE Hauptaktion je Bildschirm, und das Detail hat seine. Auch hier kein
  // navLabel -- "Kontaktwege" ohne Kontakt waere ein Menuepunkt, der nichts
  // Bestimmtes meint, also die Attrappe aus ADR-0002.
  //
  // Die SCHREIBROLLE und nicht die Leserolle. Dass beide Konstanten heute
  // denselben Wert tragen, ist kein Grund, hier die falsche zu nennen: wird die
  // Leserolle eines Tages eigenstaendig, darf dieser Bildschirm nicht
  // mitwandern.
  Object.freeze({
    id: 'kontaktwege',
    path: '/kontakte/{partyId}/kontaktwege',
    title: 'Kontaktwege',
    requires: Object.freeze({ roleCode: PARTY_CAPTURE_ROLE_CODE }),
  }),
  // Die Pflege externer Kennungen (ST-PTY-003, erster Schnitt; ADR-0047).
  //
  // Dieselbe Konstruktion wie die Kontaktwege und aus denselben Gruenden:
  // eigener Bildschirm mit EINER Hauptaktion, kein navLabel (eine Kennung
  // ohne Kontakt meint nichts Bestimmtes), die SCHREIBROLLE.
  Object.freeze({
    id: 'kennungen',
    path: '/kontakte/{partyId}/kennungen',
    title: 'Kennungen',
    requires: Object.freeze({ roleCode: PARTY_CAPTURE_ROLE_CODE }),
  }),
  // Die Pflege von Adressverwendungen (ST-PTY-002, zweiter Schnitt;
  // ADR-0056). Dieselbe Konstruktion wie Kontaktwege und Kennungen: eigener
  // Bildschirm mit EINER Hauptaktion, kein navLabel, die Schreibrolle.
  Object.freeze({
    id: 'adressen',
    path: '/kontakte/{partyId}/adressen',
    title: 'Anschriften',
    requires: Object.freeze({ roleCode: PARTY_CAPTURE_ROLE_CODE }),
  }),
  // Die Namenspflege der Organisation (ST-PTY-003, zweiter Schnitt;
  // ADR-0048). Dieselbe Konstruktion: eigener Bildschirm, kein navLabel, die
  // Schreibrolle.
  Object.freeze({
    id: 'organisation',
    path: '/kontakte/{partyId}/organisation',
    title: 'Organisation',
    requires: Object.freeze({ roleCode: PARTY_CAPTURE_ROLE_CODE }),
  }),
]) as readonly Screen[];

/**
 * Was die Adresse "/" zeigt: die Kontakte, denn dort beginnt die Arbeit.
 *
 * EIN BILDSCHIRM UNTER ZWEI ADRESSEN und keine Weiterleitung. Eine
 * Weiterleitung muesste wissen, ob die Rollen schon geladen sind, und waere
 * ein zweiter Eintrag im Verlauf; die Rollen sind ohne Diagnoserolle ohnehin
 * unbekannt (ROLES_UNKNOWN), und der Leseweg entscheidet der Server. Wer ihn
 * nicht hat, bekommt dort die uebersetzte Absage mit naechstem Schritt.
 */
export const HOME: Screen = SCREENS.find((screen) => screen.id === 'kontakte') as Screen;

export type NavGroupId = 'arbeit' | 'versand' | 'datenpflege';

/** Die Gruppen der Seitennavigation, in ihrer Reihenfolge. */
export const NAV_GROUPS: readonly { readonly id: NavGroupId; readonly label: string }[] = Object.freeze([
  Object.freeze({ id: 'arbeit' as const, label: 'Arbeiten' }),
  // Eigene Gruppe: sie waechst mit den Kampagnen (Entscheidung Dirigent 12.10.2026).
  Object.freeze({ id: 'versand' as const, label: 'Versand' }),
  Object.freeze({ id: 'datenpflege' as const, label: 'Datenpflege' }),
]);

/**
 * Die beiden Zustaende VOR der Anmeldung.
 *
 * Sie stehen bewusst NICHT in `SCREENS`, und das ist der ganze Punkt: dorthin
 * kann niemand navigieren, sie haben keine eigene Adresse, und ein Eintrag in
 * der Navigation waere ein Ziel, das es nicht gibt. Der Rahmen braucht sie
 * trotzdem als Bildschirm, weil er Titel und Ueberschrift von dort nimmt --
 * ohne sie zeigte er waehrend der Anmeldepruefung "Seite nicht gefunden".
 *
 * Auf Modulebene und eingefroren: die Effekte des Rahmens haengen an der
 * IDENTITAET des Bildschirms. Bei jedem Rendern neu erzeugt waeren es jedes Mal
 * andere Objekte, und der Fenstertitel wuerde bei jedem Rendern neu gesetzt.
 */
export const CHECKING_SCREEN: Screen = Object.freeze({
  id: 'anmeldung-wird-geprueft',
  path: '/',
  navLabel: 'Anmeldung wird geprüft',
  title: 'Anmeldung wird geprüft',
  requires: 'signed-in',
});

export const SIGN_IN_SCREEN: Screen = Object.freeze({
  id: 'anmelden',
  path: '/',
  navLabel: 'Anmelden',
  title: 'Anmelden',
  requires: 'signed-in',
});

/**
 * Was der Rahmen ueber die Rechte der angemeldeten Person WEISS.
 *
 * Der dritte Fall ist der wichtige und der Grund, warum das hier ein eigener
 * Typ ist statt einer Liste von Rollencodes:
 *
 *   `unknown` -- die Rechte sind NICHT bekannt.
 *
 * `GET /platform/access-decision` ist die einzige Quelle fuer Rollencodes, und
 * sie verlangt selbst eine Rolle (`Liaison.Platform.Diagnostics.Read`, siehe
 * apps/api/src/policy/platform-rules.ts). Wer sie nicht hat, bekommt ein
 * Problemdokument ohne `principal` -- also KEINE Rollencodes, obwohl er
 * durchaus `Liaison.Party.Capture.Write` besitzen kann.
 *
 * Diese beiden Faelle zu verwechseln waere der teure Fehler: die
 * Schnellerfassung verschwaende fuer jemanden, der sie benutzen darf, nur weil
 * die Selbstdiagnose ihm verwehrt ist. "Keine Rolle" und "Rolle unbekannt" sind
 * nicht dasselbe, und der Unterschied gehoert deshalb in den Typ und nicht in
 * eine Bedingung, die jemand spaeter vereinfacht.
 */
export type KnownRoles = { readonly kind: 'unknown' } | { readonly kind: 'known'; readonly roleCodes: readonly string[] };

/**
 * Die Rolle, an der die Rechtequelle heute haengt.
 *
 * Sie steht hier, weil der Bildschirm "Dein Zugang" sie NENNEN muss -- nicht im
 * ersten Satz, sondern in der technischen Sicht fuer die Administration. Ohne
 * den Code bittet die Nutzerin um "die passende Rolle", und niemand weiss,
 * welche. Ein Test liest die Konstante in apps/api und vergleicht, wie bei
 * `PARTY_CAPTURE_ROLE_CODE`. Sie entfaellt mit `ROLES_UNKNOWN`, sobald die
 * Selbstauskunft nach DEC-013 steht.
 */
export const PLATFORM_DIAGNOSTICS_ROLE_CODE = 'Liaison.Platform.Diagnostics.Read';

export const ROLES_UNKNOWN: KnownRoles = Object.freeze({ kind: 'unknown' });

export function rolesKnown(roleCodes: readonly string[]): KnownRoles {
  return Object.freeze({ kind: 'known', roleCodes: Object.freeze([...roleCodes]) });
}

/**
 * Darf dieser Bildschirm angeboten werden?
 *
 * FAIL-OPEN, und das ist hier richtig -- weil es KEINE Zugriffskontrolle ist.
 * Die trifft der Server, jedes Mal, und die Negativtests der Routen belegen es.
 * Was diese Funktion entscheidet, ist allein die Frage "vergeude ich die Zeit
 * dieser Person mit einem Angebot, das sicher abgelehnt wird".
 *
 * Sicher ist die Ablehnung nur, wenn die Rollen BEKANNT sind und die noetige
 * fehlt. Bei `unknown` ist sie nicht sicher, und dann ist Anbieten die bessere
 * Wahl: der Server antwortet mit einem uebersetzten Grund und einem naechsten
 * Schritt (NFR-UX-005), waehrend ein leeres Menue keine Erklaerung traegt --
 * und Ratlosigkeit ist der schlechtere Ausgang als eine begruendete Absage.
 */
export function isOffered(screen: Screen, roles: KnownRoles): boolean {
  if (screen.requires === 'signed-in') {
    return true;
  }
  if (roles.kind === 'unknown') {
    return true;
  }
  return roles.roleCodes.includes(screen.requires.roleCode);
}

/**
 * Die Navigation der angemeldeten Person, in der Reihenfolge der Tabelle. Die
 * Gruppierung nach `navGroup` nimmt der Rahmen vor.
 */
export function offeredScreens(roles: KnownRoles): readonly Screen[] {
  return SCREENS.filter((screen) => screen.navLabel !== undefined && isOffered(screen, roles));
}

/**
 * Aufloesung einer Adresse.
 *
 * Ein abschliessender Schraegstrich wird geschluckt: `/kontakt-anlegen/` und
 * `/kontakt-anlegen` sind fuer einen Menschen dieselbe Adresse, und dieselbe
 * Normalisierung macht apps/api bereits fuer seine Routen.
 *
 * `undefined` heisst "diese Adresse gibt es nicht" und ist ein eigener Zustand,
 * kein stiller Rueckfall auf die Startseite: wer einen veralteten Link oeffnet,
 * soll erfahren, dass er veraltet ist.
 */
export interface ResolvedScreen {
  readonly screen: Screen;
  /** Werte der Platzhalter, leer bei einer Adresse ohne Platzhalter. */
  readonly parameters: Readonly<Record<string, string>>;
}

/**
 * Vergleicht eine Adresse mit einem Muster.
 *
 * Segmentweise und nicht per Regex -- dieselbe Entscheidung wie im
 * Routenvertrag der API und aus demselben Grund: ein Platzhalter darf niemals
 * ueber ein `/` hinweg greifen, und genau das ist in einer zusammengesetzten
 * Regex der Fehler, den man nicht sieht.
 *
 * Ein LEERER Platzhalterwert passt nicht. `/kontakte/` ist die Liste mit
 * abschliessendem Schraegstrich und kein Kontakt ohne Kennung; ohne diese
 * Zeile ergaebe es einen Detailbildschirm, der nichts anzuzeigen hat.
 */
function matchPath(pattern: string, pathname: string): Readonly<Record<string, string>> | undefined {
  const patternSegments = pattern.split('/');
  const pathSegments = pathname.split('/');
  if (patternSegments.length !== pathSegments.length) {
    return undefined;
  }
  const parameters: Record<string, string> = {};
  for (const [index, patternSegment] of patternSegments.entries()) {
    const pathSegment = pathSegments[index] as string;
    const placeholder = /^\{([a-zA-Z][a-zA-Z0-9]*)\}$/.exec(patternSegment);
    if (placeholder === null) {
      if (patternSegment !== pathSegment) {
        return undefined;
      }
      continue;
    }
    if (pathSegment.length === 0) {
      return undefined;
    }
    let decoded: string;
    try {
      decoded = decodeURIComponent(pathSegment);
    } catch {
      // Eine nicht dekodierbare Adresse beschreibt keinen Bildschirm, den es
      // gibt -- also "Seite nicht gefunden" und kein Absturz.
      return undefined;
    }
    parameters[placeholder[1] as string] = decoded;
  }
  return Object.freeze(parameters);
}

/**
 * Aufloesung einer Adresse.
 *
 * Ein abschliessender Schraegstrich wird geschluckt: `/kontakt-anlegen/` und
 * `/kontakt-anlegen` sind fuer einen Menschen dieselbe Adresse, und dieselbe
 * Normalisierung macht apps/api bereits fuer seine Routen.
 *
 * `undefined` heisst "diese Adresse gibt es nicht" und ist ein eigener Zustand,
 * kein stiller Rueckfall auf die Startseite: wer einen veralteten Link oeffnet,
 * soll erfahren, dass er veraltet ist.
 *
 * Die REIHENFOLGE der Tabelle entscheidet bei zwei passenden Mustern. Genau
 * ein Paar kollidiert: `/kontakte/alle` und `/kontakte/{partyId}`. Die feste
 * Adresse steht davor und gewinnt; die Probe in test/shell.test.ts zaehlt die
 * Kollisionen, und jede weitere macht sie rot.
 */
export function resolve(pathname: string): ResolvedScreen | undefined {
  const normalized = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  if (normalized === '/') {
    return { screen: HOME, parameters: Object.freeze({}) };
  }
  for (const screen of SCREENS) {
    const parameters = matchPath(screen.path, normalized);
    if (parameters !== undefined) {
      return { screen, parameters };
    }
  }
  return undefined;
}

/**
 * Der Fenstertitel.
 *
 * Bildschirm zuerst, Anwendung danach. In einer Leiste mit zwanzig Tabs ist der
 * Anfang das Einzige, was sichtbar bleibt -- "LIAISON – ..." waere dort
 * zwanzigmal dasselbe Wort.
 */
export function documentTitleFor(screen: Screen | undefined): string {
  return screen === undefined ? 'Seite nicht gefunden – LIAISON' : `${screen.title} – LIAISON`;
}
